import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import MissionDetail from "@/pages/MissionDetail";
import Artisans from "@/pages/Artisans";
import NewMission from "@/pages/NewMission";
import NewChantier from "@/pages/NewChantier";
import ChantierDetail from "@/pages/ChantierDetail";
import Premium from "@/pages/Premium";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminArtisanDetail from "@/pages/AdminArtisanDetail";
import AdminDemandeurDetail from "@/pages/AdminDemandeurDetail";
import ArtisanProfile from "@/pages/ArtisanProfile";
import Invitations from "@/pages/Invitations";
import InvitationClaim from "@/pages/InvitationClaim";
import ClientInviteLanding from "@/pages/ClientInviteLanding";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { MentionsLegales, CGU, CGV } from "@/pages/Legal";
import ArtisansCity from "@/pages/ArtisansCity";
import CityDirectory from "@/pages/CityDirectory";
import Estimator from "@/pages/Estimator";
import JoinArtisan from "@/pages/JoinArtisan";
import ForWho from "@/pages/ForWho";
import TravauxIndex from "@/pages/TravauxIndex";
import TravauxType from "@/pages/TravauxType";
import TravauxTypeCity from "@/pages/TravauxTypeCity";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import PersonaPage from "@/pages/PersonaPage";
import PersonaCityPage from "@/pages/PersonaCityPage";
import MetierIndex from "@/pages/MetierIndex";
import MetierPage from "@/pages/MetierPage";
import MetierCityPage from "@/pages/MetierCityPage";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 mono text-sm">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/artisans" element={<Artisans />} />
          <Route path="/artisans/villes" element={<CityDirectory />} />
          <Route path="/artisans/ville/:slug" element={<ArtisansCity />} />
          <Route path="/travaux" element={<TravauxIndex />} />
          <Route path="/travaux/:type" element={<TravauxType />} />
          <Route path="/travaux/:type/:city" element={<TravauxTypeCity />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/pour/:persona" element={<PersonaPage />} />
          <Route path="/pour/:persona/:city" element={<PersonaCityPage />} />
          <Route path="/artisan" element={<MetierIndex />} />
          <Route path="/artisan/:metier" element={<MetierPage />} />
          <Route path="/artisan/:metier/:city" element={<MetierCityPage />} />
          <Route path="/artisans/:id" element={<ArtisanProfile />} />
          <Route path="/invitation/:token" element={<InvitationClaim />} />
          <Route path="/inviter/:artisanId" element={<ClientInviteLanding />} />
          <Route
            path="/invitations"
            element={
              <Protected roles={["demandeur", "artisan"]}>
                <Invitations />
              </Protected>
            }
          />
          <Route
            path="/dashboard"
            element={
              <Protected>
                <Dashboard />
              </Protected>
            }
          />
          <Route
            path="/missions/new"
            element={
              <Protected roles={["demandeur"]}>
                <NewMission />
              </Protected>
            }
          />
          <Route
            path="/chantiers/new"
            element={
              <Protected roles={["demandeur"]}>
                <NewChantier />
              </Protected>
            }
          />
          <Route
            path="/chantiers/:id"
            element={
              <Protected>
                <ChantierDetail />
              </Protected>
            }
          />
          <Route
            path="/pro/premium"
            element={
              <Protected>
                <Premium />
              </Protected>
            }
          />
          <Route
            path="/missions/:id"
            element={
              <Protected>
                <MissionDetail />
              </Protected>
            }
          />
          <Route
            path="/admin"
            element={
              <Protected roles={["admin"]}>
                <AdminDashboard />
              </Protected>
            }
          />
          <Route
            path="/admin/artisans/:id"
            element={
              <Protected roles={["admin"]}>
                <AdminArtisanDetail />
              </Protected>
            }
          />
          <Route
            path="/admin/demandeurs/:id"
            element={
              <Protected roles={["admin"]}>
                <AdminDemandeurDetail />
              </Protected>
            }
          />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/estimer" element={<Estimator />} />
          <Route path="/pro/rejoindre" element={<JoinArtisan />} />
          <Route path="/pour-qui" element={<ForWho />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
